import { Form as AntdForm, Col, Row, Space, Spin, Typography } from "antd";
import useForm from "antd/lib/form/hooks/useForm";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { editRepairshop, getRepairshopInfo } from "../../apis/UserInfo/apis";
import { UserManagementQueryKeys } from "../../apis/UserInfo/apis/types";
import Button from "../../components/atoms/Button";
import Explain from "../../components/atoms/Explain";
import { FormType } from "../../components/atoms/Form/shared/enums";
import FormItemBuilder from "../../components/atoms/FormITemBuilder";
import {
  ALERT_MESSAGE,
  BUSINESS_MEDIA_ID_EXPLAIN,
  LABEL_TEXT,
  MEDIA_ID_EXPLAIN,
} from "../../components/constants";
import PreviewUploader from "../../components/molecures/PreviewUploader";
import SearchAddressInputs from "../../components/molecures/SearchAddressInputs";
import SearchAddress from "../../components/molecures/SearchAddressModal";
import TitleForm from "../../components/molecures/TitleForm";
import { RepairShopList } from "../../components/organisms/UserInfo/Master/RepairShop";
import { UserInfoServiceForm } from "../../components/organisms/UserInfo/Service";
import useModals from "../../hooks/useModals";
import useShop from "../../hooks/useShop";
import useUser from "../../hooks/useUsers";
import { Media } from "../../model/Media";
import { HttpError } from "../../network/types";
import sharedConstants from "../../shared/constants";
import { RepairShopMediaType } from "../../shared/enums";
import { numberArray } from "../../shared/options";
import { alertError, alertSuccess } from "../../shared/utils";
import { getDecodeToken } from "../../shared/utils/Token";
import { strings } from "../../shared/utils/types";
import color from "../../theme/color";
import { CountryType, SpecialCar } from "../Register/constants";
import ChangePassModal from "./components/ChangePassModal";
import ChangePhoneModal from "./components/ChangePhoneModal";
import { CONFIRM_MODAL_EXPLAIN, CONFIRM_MODAL_TITLE } from "./constants";

const { Item } = AntdForm;
const UserManagement: React.FC = () => {
  const [form] = useForm();
  const inputRef = useRef<{ [index: string]: HTMLDivElement }>({});
  const { shop } = useShop();
  const { getUserID, updateAdminInfo } = useUser();

  const { data, refetch } = useQuery(
    [UserManagementQueryKeys.GET_INFO, shop],
    () => getRepairshopInfo(),
    {
      onError: (err: HttpError) => alertError(err.message),
    }
  );

  const editMutation = useMutation(editRepairshop, {
    onSuccess: async () => {
      resetModal();
      const { data: updatedRepairShop } = await refetch();
      if (updatedRepairShop) updateAdminInfo(updatedRepairShop);
      alertSuccess(strings.SUCCESS_MODIFY);
    },
    onError: (err: HttpError) => {
      alertError(err.message);
    },
  });

  const [businessMedia, setBusinessMedia] = useState<Media>();
  const street = AntdForm.useWatch("street", form);
  const detail = AntdForm.useWatch("detail", form);
  const managerPhone = AntdForm.useWatch("managerPhone", form);
  const { openModal, resetModal, openConfirmModal } = useModals();
  const [allMedia, setAllMedia] = useState<Media>();
  const [frontMedia, setFrontMedia] = useState<Media>();
  const [inMedia, setInMedia] = useState<Media>();
  const [waTypes, setWaTypes] = useState<CountryType[]>([]);
  const [batteryType, setBatterType] = useState<CountryType[]>([]);
  const [engineOilType, setEngineOilType] = useState<CountryType[]>([]);
  const [specialCars, setSpeceialCars] = useState<SpecialCar[]>([]);
  const [lightRepairs, setLightRepair] = useState<CountryType[]>([]);
  const [twentyInchTireHandleable, setTwentyInchTireHandleable] = useState<
    boolean | undefined
  >(true);
  const [rFTHandleable, setRFTHandleable] = useState<boolean | undefined>(true);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (data) {
      /**
       * 최소,최댓값 가지고 파싱
       * @TODO 서버 배포 되면 제거할 것
       */
      const tireOptions = data.repairShopOption?.options?.services?.tire;

      const minInch: number | undefined = tireOptions?.minInch || undefined;
      const maxInch: number | undefined = tireOptions?.maxInch || undefined;

      form.setFieldsValue({
        ...data,

        subPhone1:
          data.subManagerPhoneList?.length > 0
            ? data.subManagerPhoneList[0]
            : "",
        subPhone2:
          data.subManagerPhoneList?.length > 1
            ? data.subManagerPhoneList[1]
            : "",

        tireSize: [minInch, maxInch],
      });
      if (
        data.repairShopMedias.find(
          (val) => val.type === RepairShopMediaType.ALL
        )
      )
        setAllMedia(
          data.repairShopMedias.find(
            (val) => val.type === RepairShopMediaType.ALL
          )?.media
        );
      if (
        data.repairShopMedias.find(
          (val) => val.type === RepairShopMediaType.FRONT
        )
      )
        setFrontMedia(
          data.repairShopMedias.find(
            (val) => val.type === RepairShopMediaType.FRONT
          )?.media
        );
      if (
        data.repairShopMedias.find((val) => val.type === RepairShopMediaType.IN)
      )
        setInMedia(
          data.repairShopMedias.find(
            (val) => val.type === RepairShopMediaType.IN
          )?.media
        );
      setBusinessMedia(data.businessMedia);

      setRFTHandleable(data.rFTHandleable);
      setTwentyInchTireHandleable(data.twentyInchTireHandleable);
      const waTypes = [];
      if (data.domesticWAHandleable) waTypes.push(CountryType.Domestic);
      if (data.foreignWAHandleable) waTypes.push(CountryType.Foreign);

      const batteryType = [];
      if (data.domesticBatteryExchangeable)
        batteryType.push(CountryType.Domestic);
      if (data.foreignBatteryExchangeable)
        batteryType.push(CountryType.Foreign);
      const engineOilType = [];
      if (data.domesticEngineOilExchangeable)
        engineOilType.push(CountryType.Domestic);
      if (data.foreignEngineOilExchangeable)
        engineOilType.push(CountryType.Foreign);
      const specialCars = [];
      if (data.bongoAcceptable) specialCars.push(SpecialCar.BONGO);
      if (data.porterHighTopAcceptable)
        specialCars.push(SpecialCar.PORTER_HIGH_TOP);
      if (data.porterLowTopAcceptable)
        specialCars.push(SpecialCar.PORTER_LOW_TOP);
      const lightRepairs = [];
      if (data.domesticLightRepairHandleable)
        lightRepairs.push(CountryType.Domestic);
      if (data.foreignLightRepairHandleable)
        lightRepairs.push(CountryType.Foreign);

      setWaTypes(waTypes);
      setBatterType(batteryType);
      setEngineOilType(engineOilType);
      setSpeceialCars(specialCars);
      setLightRepair(lightRepairs);
    }
  }, [data]);

  const isMaster = useMemo(() => {
    const info = getDecodeToken();

    return info?.isMaster && +shop === getUserID();
  }, [shop]);

  return data ? (
    <>
      <Space
        style={{
          position: "absolute",
          marginTop: -56,
          right: 0,
        }}
        align="center"
      >
        <Button
          type="default"
          style={{ background: color.gray12 }}
          ghost
          onClick={() =>
            openConfirmModal({
              contents: CONFIRM_MODAL_EXPLAIN.MODIFY_RESET,
              title: CONFIRM_MODAL_TITLE.MODIFY_RESET,
              onOk: () => {
                resetModal();
                window.location.reload();
                form.resetFields();
              },
            })
          }
        >
          초기화
        </Button>
        <Button
          type="primary"
          onClick={() => {
            form.submit();
            if (!detail || !street) {
              inputRef.current.detail.scrollIntoView();
            }
          }}
        >
          저장하기
        </Button>
      </Space>
      <AntdForm
        labelCol={{
          flex: "0 0 165px",
          style: {
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "24px",
          },
        }}
        validateTrigger={["onChange", "onSubmit", "onBlur"]}
        form={form}
        labelAlign="left"
        requiredMark={false}
        wrapperCol={{ style: { marginBottom: "0px" } }}
        colon={false}
        scrollToFirstError={{
          scrollMode: "always",
          behavior: "smooth",
          block: "center",
        }}
        validateMessages={{
          required: "필수 항목 입니다.",
        }}
        onFinish={(value) => {
          if (!businessMedia) {
            inputRef.current.businessMedia.scrollIntoView();
          } else if (!allMedia) {
            inputRef.current.allMedia.scrollIntoView();
          } else if (!frontMedia) {
            inputRef.current.frontMedia.scrollIntoView();
          } else if (!inMedia) {
            inputRef.current.inMedia.scrollIntoView();
          } else {
            const newPhoneList: string[] = [];
            if (value.subPhone1) newPhoneList?.push(value.subPhone1);
            if (value.subPhone2) newPhoneList?.push(value.subPhone2);

            openConfirmModal({
              contents: CONFIRM_MODAL_EXPLAIN.USER_SAVE,
              title: CONFIRM_MODAL_TITLE.USER_SAVE,
              onOk: () => {
                editMutation.mutate({
                  ...value,
                  businessMediaId: businessMedia?.id,
                  repairShopMedias: [
                    {
                      repairShopMediaId: allMedia?.id,
                      type: RepairShopMediaType.ALL,
                    },
                    {
                      repairShopMediaId: frontMedia?.id,
                      type: RepairShopMediaType.FRONT,
                    },
                    {
                      repairShopMediaId: inMedia?.id,
                      type: RepairShopMediaType.IN,
                    },
                  ],
                  subManagerPhoneList: newPhoneList,
                  foreignWAHandleable: waTypes.includes(CountryType.Foreign),
                  domesticWAHandleable: waTypes.includes(CountryType.Domestic),
                  rFTHandleable,
                  twentyInchTireHandleable,
                  domesticEngineOilExchangeable: engineOilType.includes(
                    CountryType.Domestic
                  ),
                  domesticBatteryExchangeable: batteryType.includes(
                    CountryType.Domestic
                  ),
                  foreignEngineOilExchangeable: engineOilType.includes(
                    CountryType.Foreign
                  ),
                  foreignBatteryExchangeable: batteryType.includes(
                    CountryType.Foreign
                  ),
                  foreignLightRepairHandleable: lightRepairs.includes(
                    CountryType.Foreign
                  ),
                  domesticLightRepairHandleable: lightRepairs.includes(
                    CountryType.Domestic
                  ),
                  bongoAcceptable: specialCars.includes(SpecialCar.BONGO),
                  porterLowTopAcceptable: specialCars.includes(
                    SpecialCar.PORTER_LOW_TOP
                  ),
                  porterHighTopAcceptable: specialCars.includes(
                    SpecialCar.PORTER_HIGH_TOP
                  ),
                });
              },
              onCancel: resetModal,
            });
          }
        }}
      >
        <Space direction="vertical" size={20} style={{ display: "flex" }}>
          <TitleForm title="기본정보">
            <FormItemBuilder
              forminstances={form}
              forms={[
                {
                  type: FormType.custom,
                  span: 24,
                  label: "아이디",

                  key: "repairshopAId",
                  isRequired: false,
                  render: () => {
                    return data?.repairShopAId;
                  },
                },
                {
                  type: FormType.custom,
                  span: 24,
                  label: "비밀번호",
                  key: "changePwdBtn",

                  render: () => {
                    return (
                      <Button
                        onClick={() => {
                          openModal(<ChangePassModal />);
                        }}
                        ghost
                      >
                        비밀번호 변경
                      </Button>
                    );
                  },
                },
                {
                  type: FormType.input,
                  span: 24,
                  label: "상호명",
                  key: "displayName",
                  itemProps: {
                    rules: [
                      {
                        required: true,
                        message: ALERT_MESSAGE.NULL_REQUIRED,
                      },
                    ],
                  },
                  placeholder: "상호명을 입력해주세요",
                },
                {
                  type: FormType.input,
                  span: 24,
                  label: "담당자 성함",
                  itemProps: {
                    rules: [
                      {
                        required: true,
                        message: ALERT_MESSAGE.NULL_REQUIRED,
                      },
                    ],
                  },
                  key: "managerName",
                  placeholder: "담당자 성함을 입력해주세요",
                },
                {
                  type: FormType.custom,
                  span: 24,
                  label: "휴대폰 번호",
                  key: "managerPhone",
                  itemProps: {
                    rules: [
                      {
                        required: true,
                        message: ALERT_MESSAGE.NULL_REQUIRED,
                      },
                    ],
                  },
                  placeholder: "휴대폰 번호를 입력해주세요",
                  render: () => {
                    return (
                      <Row
                        gutter={managerPhone ? [10, 0] : [0, 0]}
                        align="middle"
                      >
                        <Item noStyle name="managerPhone" />
                        <Col>
                          {managerPhone?.replace(
                            /(^02.{0}|^01.{1}|[0-9]{3})([0-9]+)([0-9]{4})/,
                            "$1-$2-$3"
                          )}
                        </Col>
                        <Col>
                          <Button
                            onClick={() => {
                              openModal(
                                <ChangePhoneModal
                                  managerPhone={managerPhone || "01041011630"}
                                  setFieldValue={form.setFieldsValue}
                                />
                              );
                            }}
                            ghost
                          >
                            휴대폰 번호 변경
                          </Button>
                        </Col>
                      </Row>
                    );
                  },
                },
                {
                  type: FormType.input,
                  label: "예비 번호1",
                  key: "subPhone1",
                  placeholder: "예비 번호를 입력해주세요",
                  isRequired: false,
                  itemProps: {
                    rules: [
                      {
                        pattern: /^(\d{2,3})(\d{3,4})(\d{4})$/,
                        message: ALERT_MESSAGE.WRONG_PHONE,
                      },
                    ],
                  },
                },
                {
                  type: FormType.input,
                  label: "예비 번호2",
                  key: "subPhone2",
                  placeholder: "예비 번호를 입력해주세요",
                  isRequired: false,

                  itemProps: {
                    rules: [
                      {
                        pattern: /^(\d{2,3})(\d{3,4})(\d{4})$/,
                        message: ALERT_MESSAGE.WRONG_PHONE,
                      },
                    ],
                  },
                },
                {
                  type: FormType.input,
                  label: "이메일 주소",
                  key: "managerEmail",
                  itemProps: {
                    rules: [
                      {
                        pattern:
                          /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i,
                        message: ALERT_MESSAGE.WRONG_EMAIL,
                      },
                      {
                        required: true,
                        message: ALERT_MESSAGE.NULL_REQUIRED,
                      },
                    ],
                  },
                  placeholder: "이메일 주소를 입력해주세요",
                },
                {
                  type: FormType.input,
                  itemProps: {
                    rules: [
                      {
                        pattern: /^[0-9]{2,3}[0-9]{3,4}[0-9]{4}/,
                        message: ALERT_MESSAGE.WRONG_TEL,
                      },
                      {
                        required: true,
                        message: ALERT_MESSAGE.NULL_REQUIRED,
                      },
                    ],
                  },
                  label: "매장 대표번호",
                  key: "tel",
                  placeholder: "매장 대표번호를 입력해주세요",
                },
                {
                  type: FormType.custom,
                  label: "사업자등록번호",
                  key: "businessRegistrationNumber",
                  isRequired: false,
                  render: () => {
                    return (
                      <>
                        <Col>{data?.businessRegistrationNumber}</Col>
                        <Explain
                          explain="※ 사업자등록번호 변경은 고객센터로 문의해주세요."
                          color={color.red1}
                        />
                      </>
                    );
                  },
                },

                {
                  type: FormType.custom,
                  label: "매장 주소",
                  key: "detail",
                  placeholder: "매장 주소를 입력해주세요",
                  itemProps: {
                    rules: [
                      {
                        required: true,
                        message: ALERT_MESSAGE.NULL_REQUIRED,
                      },
                    ],
                  },
                  render: () => {
                    return (
                      <>
                        <Item noStyle name="street" />
                        <Item noStyle name="detail" />

                        <div
                          ref={(ref: HTMLDivElement) => {
                            inputRef.current.detail = ref;
                          }}
                        />
                        <SearchAddressInputs
                          street={street}
                          detail={detail}
                          onChange={(e) => {
                            form.setFieldsValue({ detail: e.target.value });
                          }}
                          onClick={() => {
                            openModal(
                              <SearchAddress
                                setShowSearchAddress={resetModal}
                                setAddress={(e) => {
                                  form.setFieldsValue({
                                    zibun: e.zibun,
                                    zipCode: e.zipCode,
                                    street: e.street,
                                    country: e.country,

                                    detail: e.detail,
                                    regionDepth1: e.regionDepth1,
                                    regionDepth2: e.regionDepth2,
                                    regionDepth3: e.regionDepth3,
                                  });
                                }}
                              />
                            );
                          }}
                        />
                      </>
                    );
                  },
                },
                {
                  type: FormType.input,
                  label: "사업자 등록증 상호명",
                  key: "repairShopName",
                  isRequired: false,
                  placeholder: "사업자 등록증 상호명을 입력해주세요",
                },
                {
                  type: FormType.custom,
                  label: LABEL_TEXT.BUSINESS_MEDIA_ID,
                  key: "businessMedia",

                  render: () => (
                    <>
                      <div
                        ref={(ref: HTMLDivElement) => {
                          inputRef.current.businessMedia = ref;
                        }}
                      />
                      <PreviewUploader
                        folder="tirepick_repairshop_web_business_img/"
                        max={1}
                        imageList={businessMedia ? [businessMedia] : []}
                        onUpload={(e) => {
                          setBusinessMedia(e[0]);
                        }}
                        onRemove={() => {
                          setBusinessMedia(undefined);
                        }}
                        inputKey="business"
                      />
                      <Explain explain={BUSINESS_MEDIA_ID_EXPLAIN} />
                      <Explain
                        status={!businessMedia ? "error" : undefined}
                        explain={
                          !businessMedia
                            ? sharedConstants.message.ALERT_MESSAGE
                                .NULL_REQUIRED
                            : undefined
                        }
                      />
                    </>
                  ),
                },
              ]}
            />
          </TitleForm>

          {!isMaster && (
            <TitleForm title="신청서 질문답변">
              <FormItemBuilder
                forminstances={form}
                forms={[
                  {
                    type: FormType.inputNumber,

                    key: "maximumWeekdayVehicleCount",
                    label: "평일 최대 차량 수",
                  },
                  {
                    type: FormType.inputNumber,
                    key: "maximumWeekendVehicleCount",
                    label: "주말 최대 차량 수",
                  },
                ]}
              />
            </TitleForm>
          )}
          {!isMaster && (
            <TitleForm title="매장 환경">
              <FormItemBuilder
                forminstances={form}
                forms={[
                  {
                    type: FormType.select,
                    key: "liftCount",
                    label: "보유 리프트(개)",
                    itemProps: {
                      rules: [
                        {
                          required: true,
                          message: ALERT_MESSAGE.NULL_REQUIRED,
                        },
                      ],
                    },
                    options: numberArray(10),
                  },
                  {
                    type: FormType.select,
                    key: "employeeCount",
                    label: "보유 정비인력(명)",
                    options: numberArray(10),
                    itemProps: {
                      rules: [
                        {
                          required: true,
                          message: ALERT_MESSAGE.NULL_REQUIRED,
                        },
                      ],
                    },
                  },
                  {
                    type: FormType.radio,
                    key: "waitingRoomExistence",
                    label: "고객 대기실 보유 여부",
                    options: [
                      { label: "보유", value: true },
                      { label: "미보유", value: false },
                    ],
                  },
                  {
                    type: FormType.custom,
                    key: "allMedia",
                    label: LABEL_TEXT.MEDIA_IDS,
                    render: () => {
                      return (
                        <>
                          <AntdForm.Item noStyle name="zibun" />
                          <AntdForm.Item noStyle name="zipcode" />
                          <AntdForm.Item noStyle name="regionDepth1" />
                          <AntdForm.Item noStyle name="regionDepth2" />
                          <AntdForm.Item noStyle name="regionDepth3" />
                          <AntdForm.Item noStyle name="repairShopName" />
                          <div
                            ref={(ref: HTMLDivElement) => {
                              inputRef.current.allMedia = ref;
                            }}
                          />
                          <PreviewUploader
                            folder="tirepick_repairshop_web_repair_shop_media/"
                            max={1}
                            multiple
                            label="대표"
                            imageList={allMedia ? [allMedia] : []}
                            inputKey="repairshop_media_all"
                            onUpload={(e) => {
                              setAllMedia(e[0]);
                            }}
                            onRemove={() => {
                              setAllMedia(undefined);
                            }}
                          />
                          <Explain explain={MEDIA_ID_EXPLAIN} />
                          <Explain
                            status={
                              allMedia === undefined ? "error" : undefined
                            }
                            explain={
                              allMedia === undefined
                                ? sharedConstants.message.ALERT_MESSAGE
                                    .NULL_REQUIRED
                                : undefined
                            }
                          />
                        </>
                      );
                    },
                  },
                  {
                    type: FormType.custom,
                    key: "frontMedia",
                    label: LABEL_TEXT.MEDIA_IDS,
                    render: () => {
                      return (
                        <>
                          {" "}
                          <div
                            ref={(ref: HTMLDivElement) => {
                              inputRef.current.frontMedia = ref;
                            }}
                          />
                          <AntdForm.Item noStyle name="zibun" />
                          <AntdForm.Item noStyle name="zipcode" />
                          <AntdForm.Item noStyle name="regionDepth1" />
                          <AntdForm.Item noStyle name="regionDepth2" />
                          <AntdForm.Item noStyle name="regionDepth3" />
                          <PreviewUploader
                            folder="tirepick_repairshop_web_repair_shop_media/"
                            max={1}
                            multiple
                            imageList={frontMedia ? [frontMedia] : []}
                            inputKey="repairshop_media_front"
                            onUpload={(e) => {
                              setFrontMedia(e[0]);
                            }}
                            onRemove={() => {
                              setFrontMedia(undefined);
                            }}
                          />
                          <Explain explain={MEDIA_ID_EXPLAIN} />
                          <Explain
                            status={
                              frontMedia === undefined ? "error" : undefined
                            }
                            explain={
                              frontMedia === undefined
                                ? sharedConstants.message.ALERT_MESSAGE
                                    .NULL_REQUIRED
                                : undefined
                            }
                          />
                        </>
                      );
                    },
                  },
                  {
                    type: FormType.custom,
                    key: "inMedia",
                    label: LABEL_TEXT.MEDIA_IDS,
                    render: () => {
                      return (
                        <>
                          {" "}
                          <div
                            ref={(ref: HTMLDivElement) => {
                              inputRef.current.inMedia = ref;
                            }}
                          />
                          <AntdForm.Item noStyle name="zibun" />
                          <AntdForm.Item noStyle name="zipcode" />
                          <AntdForm.Item noStyle name="regionDepth1" />
                          <AntdForm.Item noStyle name="regionDepth2" />
                          <AntdForm.Item noStyle name="regionDepth3" />
                          <PreviewUploader
                            folder="tirepick_repairshop_web_repair_shop_media/"
                            max={1}
                            multiple
                            imageList={inMedia ? [inMedia] : []}
                            inputKey="repairshop_media_in"
                            onUpload={(e) => {
                              setInMedia(e[0]);
                            }}
                            onRemove={() => {
                              setInMedia(undefined);
                            }}
                          />
                          <Explain explain={MEDIA_ID_EXPLAIN} />
                          <Explain
                            status={inMedia === undefined ? "error" : undefined}
                            explain={
                              inMedia === undefined
                                ? sharedConstants.message.ALERT_MESSAGE
                                    .NULL_REQUIRED
                                : undefined
                            }
                          />
                        </>
                      );
                    },
                  },
                ]}
              />
            </TitleForm>
          )}
          {!isMaster && (
            <TitleForm title="정비가능 항목">
              <UserInfoServiceForm form={form} consignmentServicesReadonly />
            </TitleForm>
          )}
          {!isMaster && (
            <TitleForm title="추가사항">
              <FormItemBuilder
                forms={[
                  {
                    type: FormType.textArea,
                    key: "moreDetail",
                    label: LABEL_TEXT.ADDITIONAL,
                    isRequired: false,
                    placeholder:
                      "다수 가맹점 보유 등 추가로 입력하고 싶은 사항이 있으면 입력해주세요.",
                  },
                  {
                    type: FormType.input,
                    key: "introduction",
                    label: LABEL_TEXT.ONE_SENTENCE,
                    isRequired: false,
                    placeholder: "매장 한마디를 입력해주세요",
                  },
                ]}
                forminstances={form}
              />
            </TitleForm>
          )}

          {isMaster && (
            <TitleForm title="관리 매장">
              <RepairShopList />
            </TitleForm>
          )}
        </Space>
      </AntdForm>
    </>
  ) : (
    <Spin spinning />
  );
};
export default UserManagement;
